import React, { useCallback } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { SmileyOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { mergeOverrides, Popover, Tooltip, Touchable } from '@lyearn/ui';
import EmojiPicker from '@/components/EmojiPicker';

import { emojiStyles } from './styles';
import { ChildrenRendererProps, ReactionSelectorProps } from './types';
import { DEFAULTS, getMenuButtonClassName } from './utils';

const PICKER_STYLES = {
  border: 'none',
};

const defaultChildren = ({ overrides }: ChildrenRendererProps) => (
  <SmileyOutline className={overrides?.className} />
);

function ReactionSelector(props: ReactionSelectorProps) {
  const { t } = useTranslation();
  const {
    onSelect,
    className,
    overrides,
    children,
    tooltipPlacement,
    useAnchorAsContainer = false,
    tooltipTitle = t('Common:Message.AddReaction'),
  } = props;
  const [open, { toggle }] = useToggle(false);
  const mergedOverrides = mergeOverrides(DEFAULTS, overrides);

  const _onSelect = useCallback(
    (emoji: any) => {
      toggle(false);
      emoji.id && onSelect(emoji.id, emoji);
    },
    [onSelect, toggle],
  );

  return (
    <Popover open={open} onOpenChange={toggle}>
      <Tooltip placement={tooltipPlacement} title={tooltipTitle!}>
        <Popover.Button
          className={cn(
            'reaction-selector',
            className,
            getMenuButtonClassName(open, mergedOverrides?.MenuButton?.classes),
          )}>
          <Touchable>{children || defaultChildren({ overrides: mergedOverrides?.Icon })}</Touchable>
        </Popover.Button>
      </Tooltip>
      <React.Suspense fallback={null}>
        <Popover.Content
          className={emojiStyles}
          minWidth={30}
          position="top"
          useAnchorAsContainer={useAnchorAsContainer}>
          <EmojiPicker style={PICKER_STYLES} onSelect={_onSelect} {...mergedOverrides?.Picker} />
        </Popover.Content>
      </React.Suspense>
    </Popover>
  );
}

export default React.memo(ReactionSelector);
